import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, fmtDate, errMsg, curSym } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { LockedPeriodBanner } from "@finance/components/LockedPeriodBanner";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { BulkUploadButton } from "@finance/components/BulkUpload";

const today = () => new Date().toISOString().slice(0, 10);

export default function OpeningBalances() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bankAccts, setBankAccts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [gl, setGl] = useState({ date: today(), side: "debit" });
  const [party, setParty] = useState({ party_type: "client", date: today() });
  const [items, setItems] = useState([{ reference: "", date: today(), amount: "" }]);
  const [check, setCheck] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(() => {
    api.get("/opening-balances").then((r) => setRows(r.data));
    api.get("/opening-balances/check").then((r) => setCheck(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/gl/accounts").then((r) => setAccounts(r.data));
    api.get("/bank-accounts").then((r) => setBankAccts(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, [load]);

  const glCur = (bankAccts.find((b) => b.gl_account_code === gl.account_code) || {}).currency || "TZS";
  const glIsFx = glCur !== "TZS";

  const saveGl = async () => {
    if (!gl.account_code || !gl.amount) { toast.error("Account and amount required"); return; }
    if (glIsFx && !(Number(gl.fx_rate) > 0)) { toast.error(`Enter the FX rate (1 ${glCur} = ? TZS) for this account`); return; }
    try {
      await api.post("/opening-balances/gl", {
        account_code: gl.account_code, date: gl.date, reference: gl.reference,
        currency: glCur, fx_rate: glIsFx ? Number(gl.fx_rate) : null,
        debit: gl.side === "debit" ? Number(gl.amount) : 0,
        credit: gl.side === "credit" ? Number(gl.amount) : 0,
      });
      toast.success("GL opening balance posted");
      setGl({ date: today(), side: "debit" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveParty = async () => {
    if (!party.party_id) { toast.error("Select a party"); return; }
    const clean = items.filter((it) => Number(it.amount) > 0).map((it) => ({ reference: it.reference, date: it.date, amount: Number(it.amount) }));
    if (!clean.length) { toast.error("Add at least one line with an amount"); return; }
    try {
      const { data } = await api.post("/opening-balances/party", { party_type: party.party_type, party_id: party.party_id, items: clean });
      toast.success(`${data.created} opening item(s) posted`);
      setParty({ party_type: party.party_type, date: today() }); setItems([{ reference: "", date: today(), amount: "" }]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this opening balance and reverse its ledger entry?")) return;
    try { await api.delete(`/opening-balances/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const partyList = party.party_type === "client" ? clients : suppliers;

  if (!isOwner) return (
    <Layout><PageHeader title="Opening Balances" subtitle="Owner (Ronick) only" />
      <div className="p-8"><div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-500">Only Ronick can view and enter opening balances.</div></div>
    </Layout>
  );

  return (
    <Layout>
      <PageHeader title="Opening Balances" subtitle="Set opening balances without disturbing existing transactions. Posted against Opening Balance Equity (3900)."
        actions={<BulkUploadButton entity="opening_balances" label="Bulk Upload" onDone={load} />} />
      <div className="p-8 space-y-8" data-testid="opening-balances-page">
        {check && check.count > 0 && (
          <div data-testid="trial-balance-banner" className={`flex items-center gap-2 rounded-sm border px-4 py-2 text-sm ${check.balanced ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            {check.balanced
              ? <span><strong>Opening balances balanced ✓</strong> — Opening Balance Equity (3900) is zero.</span>
              : <span><strong>Opening balances not yet balanced</strong> — Opening Balance Equity (3900) residual is TSh {fmtNum(check.opening_equity_balance)}. This should reach zero once every opening entry has its counterpart.</span>}
          </div>
        )}
        {/* GL opening balance */}
        <div className="rounded-sm border border-gray-200 bg-white p-5">
          <div className="lx-label mb-3">GL Account Opening Balance</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="md:col-span-2">
              <Label className="lx-label">Account</Label>
              <div className="mt-1 flex items-center gap-1">
                <Select value={gl.account_code || ""} onValueChange={(v) => setGl({ ...gl, account_code: v })}>
                  <SelectTrigger className="flex-1" data-testid="ob-gl-account"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                </Select>
                <QuickAddButton type="account" onCreated={(a) => { api.get("/gl/accounts").then((r) => setAccounts(r.data)); setGl((s) => ({ ...s, account_code: a.code })); }} />
              </div>
            </div>
            <div><Label className="lx-label">Side</Label>
              <Select value={gl.side} onValueChange={(v) => setGl({ ...gl, side: v })}>
                <SelectTrigger className="mt-1" data-testid="ob-gl-side"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="lx-label">Amount{glIsFx ? ` (${glCur})` : ""}</Label><Input type="number" className="mt-1" data-testid="ob-gl-amount" value={gl.amount || ""} onChange={(e) => setGl({ ...gl, amount: e.target.value })} /></div>
            <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="ob-gl-date" value={gl.date} onChange={(e) => setGl({ ...gl, date: e.target.value })} /></div>
          </div>
          {glIsFx && (
            <div className="mt-3 flex items-end gap-3 rounded-sm border border-amber-200 bg-amber-50/50 p-3" data-testid="ob-gl-fx-row">
              <div className="w-56"><Label className="lx-label text-amber-700">FX Rate — 1 {glCur} = ? TZS</Label><Input type="number" className="mt-1" data-testid="ob-gl-fx" value={gl.fx_rate || ""} onChange={(e) => setGl({ ...gl, fx_rate: e.target.value })} placeholder="e.g. 2600" /></div>
              <div className="text-sm text-gray-600">Converts to base currency: <b className="lx-num">TSh {fmtNum((Number(gl.amount) || 0) * (Number(gl.fx_rate) || 0))}</b></div>
            </div>
          )}
          <div className="mt-3 flex items-end gap-3">
            <div className="flex-1"><Label className="lx-label">Reference</Label><Input className="mt-1" data-testid="ob-gl-ref" value={gl.reference || ""} onChange={(e) => setGl({ ...gl, reference: e.target.value })} placeholder="e.g. Opening cash at bank" /></div>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ob-gl-save" onClick={saveGl}>Post GL Opening</Button>
          </div>
          <LockedPeriodBanner date={gl.date} className="mt-3" />
        </div>

        {/* Party opening balance with breakdown */}
        <div className="rounded-sm border border-gray-200 bg-white p-5">
          <div className="lx-label mb-3">Party Opening Balance (breakdown for allocation)</div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div><Label className="lx-label">Party Type</Label>
              <Select value={party.party_type} onValueChange={(v) => setParty({ ...party, party_type: v, party_id: undefined })}>
                <SelectTrigger className="mt-1" data-testid="ob-party-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="client">Client (Debtor / AR)</SelectItem><SelectItem value="supplier">Supplier (Creditor / AP)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label className="lx-label">{party.party_type === "client" ? "Client" : "Supplier"}</Label>
              <div className="mt-1 flex items-center gap-1">
                <Select value={party.party_id || ""} onValueChange={(v) => setParty({ ...party, party_id: v })}>
                  <SelectTrigger className="flex-1" data-testid="ob-party"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{partyList.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <QuickAddButton type={party.party_type} onCreated={(p) => {
                  const url = party.party_type === "client" ? "/clients" : "/suppliers";
                  api.get(url).then((r) => party.party_type === "client" ? setClients(r.data) : setSuppliers(r.data));
                  setParty((s) => ({ ...s, party_id: p.id }));
                }} />
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="lx-label text-gray-500">Outstanding items (each becomes an allocatable open item)</div>
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`ob-item-${i}`}>
                <Input className="flex-1" placeholder="Reference / Invoice No" data-testid={`ob-item-ref-${i}`} value={it.reference} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, reference: e.target.value } : x))} />
                <DateField className="w-40" data-testid={`ob-item-date-${i}`} value={it.date} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} />
                <Input type="number" className="w-40" placeholder="Amount" data-testid={`ob-item-amt-${i}`} value={it.amount} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                <button className="text-gray-300 hover:text-red-500" data-testid={`ob-item-del-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash size={16} /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="ob-item-add" onClick={() => setItems([...items, { reference: "", date: today(), amount: "" }])}><Plus size={14} className="mr-1" />Add line</Button>
          </div>
          <div className="mt-3 flex justify-end">
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ob-party-save" onClick={saveParty}>Post Party Opening</Button>
          </div>
        </div>

        {/* Existing */}
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-left">Reference / Account</th>
              <th className="px-4 py-2 text-left">Party</th><th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-right">Amount</th><th className="px-4 py-2 text-right">Remaining</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <TableEmpty colSpan={7} title="No opening balances yet" message="Set your opening trial balance to seed the ledger." />}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`ob-row-${r.id}`} className="border-b border-gray-100">
                  <td className="px-4 py-2"><span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs">{r.kind === "gl" ? "GL" : r.party_type === "client" ? "AR" : "AP"}</span></td>
                  <td className="px-4 py-2">{r.kind === "gl" ? `${r.account_code} · ${r.account_name || ""}` : (r.reference || "Opening")}</td>
                  <td className="px-4 py-2 text-gray-600">{r.party_name || "—"}</td>
                  <td className="px-4 py-2 lx-num">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2 text-right lx-num">{r.kind === "gl"
                    ? <>{curSym(r.currency)} {fmtNum((r.currency && r.currency !== "TZS") ? ((r.debit_fc || 0) || (r.credit_fc || 0)) : ((r.debit || 0) || (r.credit || 0)))}<span className="ml-1 text-[10px] text-gray-400">{r.debit ? "Dr" : "Cr"}</span>{(r.currency && r.currency !== "TZS") ? <div className="text-[10px] font-normal text-gray-400">≈ TSh {fmtNum((r.debit || 0) || (r.credit || 0))} @ {fmtNum(r.fx_rate)}</div> : null}</>
                    : <>{fmtNum(r.amount)}</>}</td>
                  <td className="px-4 py-2 text-right lx-num">{r.kind === "party" ? fmtNum(r.remaining) : "—"}</td>
                  <td className="px-4 py-2 text-right"><div className="flex items-center justify-end gap-2">
                    <button className="text-gray-300 hover:text-[#1B4383]" data-testid={`ob-edit-${r.id}`} onClick={() => setEditRow(r)}><PencilSimple size={14} /></button>
                    <button className="text-gray-300 hover:text-red-500" data-testid={`ob-del-${r.id}`} onClick={() => del(r.id)}><Trash size={14} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <EditOpeningDialog row={editRow} bankAccts={bankAccts} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />
    </Layout>
  );
}

function EditOpeningDialog({ row, bankAccts, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!row) { setF(null); return; }
    if (row.kind === "gl") {
      const side = (row.debit_fc || row.debit) ? "debit" : "credit";
      const amount = (row.currency && row.currency !== "TZS") ? ((row.debit_fc || 0) || (row.credit_fc || 0)) : ((row.debit || 0) || (row.credit || 0));
      setF({ side, amount, fx_rate: row.fx_rate || "", date: (row.date || "").slice(0, 10), reference: row.reference || "" });
    } else {
      setF({ party_amount: row.amount, party_reference: row.reference || "", date: (row.date || "").slice(0, 10) });
    }
  }, [row]);
  if (!row || !f) return null;
  const glCur = row.currency || "TZS";
  const glIsFx = row.kind === "gl" && glCur !== "TZS";

  const save = async () => {
    try {
      if (row.kind === "gl") {
        if (!(Number(f.amount) > 0)) { toast.error("Enter an amount greater than zero"); return; }
        if (glIsFx && !(Number(f.fx_rate) > 0)) { toast.error(`Enter the FX rate (1 ${glCur} = ? TZS)`); return; }
        await api.put(`/opening-balances/${row.id}`, {
          account_code: row.account_code, side: f.side, amount: Number(f.amount),
          fx_rate: glIsFx ? Number(f.fx_rate) : null, date: f.date, reference: f.reference,
        });
      } else {
        if (!(Number(f.party_amount) > 0)) { toast.error("Enter an amount greater than zero"); return; }
        await api.put(`/opening-balances/${row.id}`, {
          party_amount: Number(f.party_amount), party_reference: f.party_reference, date: f.date,
        });
      }
      toast.success("Opening balance updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ob-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit Opening Balance</DialogTitle>
          <DialogDescription>Change the amount, rate or date — the old ledger entry is reversed and reposted.</DialogDescription>
        </DialogHeader>
        {row.kind === "gl" ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">{row.account_code} · {row.account_name || ""}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="lx-label">Side</Label>
                <Select value={f.side} onValueChange={(v) => setF({ ...f, side: v })}>
                  <SelectTrigger className="mt-1" data-testid="ob-edit-side"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="lx-label">Amount{glIsFx ? ` (${glCur})` : ""}</Label><Input type="number" className="mt-1" data-testid="ob-edit-amount" value={f.amount || ""} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
            </div>
            {glIsFx && (
              <div className="rounded-sm border border-amber-200 bg-amber-50/50 p-3" data-testid="ob-edit-fx-row">
                <Label className="lx-label text-amber-700">FX Rate — 1 {glCur} = ? TZS</Label>
                <div className="mt-1 flex items-center gap-3">
                  <Input type="number" className="w-40" data-testid="ob-edit-fx" value={f.fx_rate || ""} onChange={(e) => setF({ ...f, fx_rate: e.target.value })} />
                  <span className="text-sm text-gray-600">≈ <b className="lx-num">TSh {fmtNum((Number(f.amount) || 0) * (Number(f.fx_rate) || 0))}</b></span>
                </div>
              </div>
            )}
            <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="ob-edit-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            <div><Label className="lx-label">Reference</Label><Input className="mt-1" data-testid="ob-edit-ref" value={f.reference || ""} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">{row.party_type === "client" ? "Client (AR)" : "Supplier (AP)"} · {row.party_name || ""}</div>
            <div><Label className="lx-label">Reference</Label><Input className="mt-1" data-testid="ob-edit-party-ref" value={f.party_reference || ""} onChange={(e) => setF({ ...f, party_reference: e.target.value })} /></div>
            <div><Label className="lx-label">Amount</Label><Input type="number" className="mt-1" data-testid="ob-edit-party-amount" value={f.party_amount || ""} onChange={(e) => setF({ ...f, party_amount: e.target.value })} /></div>
            <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="ob-edit-party-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ob-edit-save" onClick={save}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
